import { getFilters } from '@client-shared/api/filters.api'
import * as ParticipantsApi from '@client-shared/api/participants.api.js'
import * as ProjectsApi from '@client-shared/api/projects.api.js'
import ignoreCanceledRequest from '@client-shared/utils/ignore-canceled-request.js'

import constants from '@/config/constants.js'

const fetchAbortControllers = {}

export default {
  namespaced: true,

  state: () => {
    return {
      project: null,
      // The project the app is loading or has loaded - set when project/fetch starts, before the project
      // is committed. The project-scoped stores drop a response for any other project (e.g. one the user
      // just left); comparing with `project` instead would also drop the responses of a project whose
      // own request failed once and is loaded again in parallel with its lists.
      requestedProjectId: null,
      isLoading: false,
      dashboardCounts: null,

      // Reflects a real server 403 (TWO_FACTOR_AUTH_REQUIRED), not a client-side prediction.
      // Cannot be derived from `project` (the server does not return it when access is blocked)
      // nor from `projects.list` (not loaded for direct links to specific pages).
      showTwoFactorAuthBlockedScreen: false,
    }
  },

  getters: {
    locationPropertiesSorted (state) {
      if (!state.project) {
        return []
      }

      return [
        'location1',
        'location2',
        'location3',
        'location4',
      ]
        .sort((a, b) => state.project.locations[a].order > state.project.locations[b].order ? 1 : -1)
    },

    locationPropertiesSortedEnabled (state, getters) {
      return [ ...getters.locationPropertiesSorted ]
        .filter(locationProperty => {
          return state.project.locations[locationProperty].isEnabled
        })
    },

    isProjectUsingLocationFeature (state, getters, rootState) {
      return state.project.computed.isLocationPropertiesEnabled || rootState.locationLayers.list.length > 0
    },

    isTaskUsingLocationTags (state, getters, rootState) {
      return task => {
        return getters.locationPropertiesSortedEnabled.find(locationProperty => task[locationProperty])
      }
    },

    isTaskUsingLocationFeature (state, getters, rootState) {
      return task => {
        const hasLocationTag = getters.isTaskUsingLocationTags(task)
        const hasLocationLayer = task.locationLayerId

        return hasLocationTag || hasLocationLayer
      }
    },

    isItemUsingLocationFeature (state, getters, rootState) {
      return item => {
        const hasLocationTag = getters.isItemUsingLocationTags(item)
        const hasLocationLayer = item.locationLayerId

        return hasLocationTag || hasLocationLayer
      }
    },

    isItemUsingLocationTags (state, getters, rootState) {
      return item => {
        return getters.locationPropertiesSortedEnabled.find(locationProperty => item[locationProperty])
      }
    },
  },

  mutations: {
    SET_PROJECT (state, project) {
      state.project = project
    },

    SET_REQUESTED_PROJECT_ID (state, projectId) {
      state.requestedProjectId = projectId
    },

    SET_IS_LOADING (state, isLoading) {
      state.isLoading = isLoading
    },

    UPDATE_PARTICIPANT (state, newParticipant) {
      state.project.participants = state.project.participants.map(p => {
        if (p._id === newParticipant._id) {
          newParticipant.user = p.user

          return newParticipant
        }

        return p
      })
    },

    // Replaces the array instead of changing it in place: Project keys its participant lookup
    // maps by the array (client-shared/models/project.model.js)
    ADD_PARTICIPANT (state, participantToAdd) {
      const hasParticipant = state.project.participants.some(participant => participant._id === participantToAdd._id)

      if (hasParticipant) {
        state.project.participants = state.project.participants.map(participant => participant._id === participantToAdd._id ? participantToAdd : participant)
      } else {
        state.project.participants = [
          ...state.project.participants,
          participantToAdd,
        ]
      }
    },

    SET_SHOW_TWO_FACTOR_AUTH_BLOCKED_SCREEN (state, isBlocked) {
      state.showTwoFactorAuthBlockedScreen = isBlocked
    },

    SET_DASHBOARD_COUNTS (state, dashboardCounts) {
      state.dashboardCounts = dashboardCounts
    },

    RESET_PROJECT (state) {
      state.project = null
      state.requestedProjectId = null
      state.showTwoFactorAuthBlockedScreen = false
      state.dashboardCounts = null
    },

    FULL_RESET (state) {
      state.project = null
      state.requestedProjectId = null
      state.showTwoFactorAuthBlockedScreen = false
      state.dashboardCounts = null
    },
  },

  actions: {
    async fetch ({
      commit,
      state,
      dispatch,
    }, projectId) {
      commit('SET_IS_LOADING', true)
      commit('SET_REQUESTED_PROJECT_ID', projectId)

      if (fetchAbortControllers.fetch instanceof AbortController) {
        fetchAbortControllers.fetch.abort()
      }

      // The fetch superseded here may already be waiting for its filters - they are the previous fetch's
      // (e.g. the project the user just left) and must not land either
      if (fetchAbortControllers.fetchFilters instanceof AbortController) {
        fetchAbortControllers.fetchFilters.abort()
      }

      const fetchAbortController = new AbortController()

      fetchAbortControllers.fetch = fetchAbortController

      const isSuperseded = () => fetchAbortControllers.fetch !== fetchAbortController

      const project = await ignoreCanceledRequest(ProjectsApi.getProject(
        {
          signal: fetchAbortController.signal,
          axios: this.$axios,
          projectId,
        }))

      // A newer project/fetch took over: fetching this project's filters now would abort the newer fetch's
      // filters request, and its project must not be committed on top of the newer one
      if (isSuperseded()) {
        return
      }

      // when the project is fetched, the socket-io events are un-blocked.
      // when running fetchFilter in parallel with fetchProject, there is a window where filter updates via web-sockets could be dropped
      // so we take this little performance penalty for data guarantee
      await dispatch('fetchFilters', { projectId })

      if (isSuperseded()) {
        return
      }

      if (project) {
        commit('SET_PROJECT', project)
        commit('SET_SHOW_TWO_FACTOR_AUTH_BLOCKED_SCREEN', false)
        commit('SET_IS_LOADING', false)
      }
    },

    async fetchDashboardCounts ({ commit }, projectId) {
      if (fetchAbortControllers.fetchDashboardCounts instanceof AbortController) {
        fetchAbortControllers.fetchDashboardCounts.abort()
      }

      fetchAbortControllers.fetchDashboardCounts = new AbortController()

      const dashboard = await ignoreCanceledRequest(ProjectsApi.getDashboard({
        signal: fetchAbortControllers.fetchDashboardCounts.signal,
        axios: this.$axios,
        projectId,
      }))

      if (dashboard) {
        commit('SET_DASHBOARD_COUNTS', dashboard.counts)
      }
    },

    async fetchFilters ({ dispatch }, { projectId }) {
      if (fetchAbortControllers.fetchFilters instanceof AbortController) {
        fetchAbortControllers.fetchFilters.abort()
      }

      fetchAbortControllers.fetchFilters = new AbortController()

      // Taken BEFORE the request: the response can only be trusted for stores whose
      // local changes were fully synced to the server when the server read them
      const syncSnapshot = this.$filterSync?.getSyncSnapshot()

      const filters = await ignoreCanceledRequest(getFilters({
        signal: fetchAbortControllers.fetchFilters.signal,
        axios: this.$axios,
        projectId,
        filterKeys: [
          constants.FILTER_KEYS.PLANS_LIST_WEB_MAIN,
          constants.FILTER_KEYS.DOCUMENTS_LIST_WEB_MAIN,
          constants.FILTER_KEYS.TASKS_LIST_WEB_DRAFTS,
          constants.FILTER_KEYS.TASKS_LIST_WEB_MAIN,
          constants.FILTER_KEYS.TASKS_LIST_WEB_DONE,
          constants.FILTER_KEYS.PHOTOS_LIST_WEB_MAIN,
          constants.FILTER_KEYS.PARTICIPANTS_LIST_WEB_MAIN,
          constants.FILTER_KEYS.ACTIVITIES_LIST_WEB,
        ],
      }))

      if (filters) {
        dispatch('commitFilters', {
          filters,
          syncSnapshot,
        })
      }
    },

    commitFilters ({ commit }, { filters, syncSnapshot }) {
      // Filter changes are synced to the server with a debounce
      // (filter-to-server-sync.js). A store with local changes the server had not seen
      // when this fetch started must keep its local state — applying the fetched copy
      // would silently revert those changes (e.g. change a filter, then click the
      // already-active tab, which re-fetches the filters).
      const canApplyServerFilter = storeName => {
        if (!this.$filterSync) {
          return true
        }

        return this.$filterSync.canApplyServerFilter({
          snapshot: syncSnapshot,
          storeName,
        })
      }
      const plansFilter = filters.find(filter => filter.filterKey === constants.FILTER_KEYS.PLANS_LIST_WEB_MAIN)
      const documentsFilter = filters.find(filter => filter.filterKey === constants.FILTER_KEYS.DOCUMENTS_LIST_WEB_MAIN)
      const taskFilterDrafts = filters.find(filter => filter.filterKey === constants.FILTER_KEYS.TASKS_LIST_WEB_DRAFTS)
      const taskFilterMain = filters.find(filter => filter.filterKey === constants.FILTER_KEYS.TASKS_LIST_WEB_MAIN)
      const taskFilterDone = filters.find(filter => filter.filterKey === constants.FILTER_KEYS.TASKS_LIST_WEB_DONE)
      const photosFilter = filters.find(filter => filter.filterKey === constants.FILTER_KEYS.PHOTOS_LIST_WEB_MAIN)
      const activitiesFilter = filters.find(filter => filter.filterKey === constants.FILTER_KEYS.ACTIVITIES_LIST_WEB)
      const participantsFilter = filters.find(filter => filter.filterKey === constants.FILTER_KEYS.PARTICIPANTS_LIST_WEB_MAIN)

      if (taskFilterDrafts && canApplyServerFilter('tasksFilterDrafts')) {
        commit('tasksFilterDrafts/SET_FILTERS', {
          filters: taskFilterDrafts.body,
          lastServerUpdate: taskFilterDrafts.modified,
        }, { root: true })
      }

      if (taskFilterMain && canApplyServerFilter('tasksFilterMain')) {
        commit('tasksFilterMain/SET_FILTERS', {
          filters: taskFilterMain.body,
          lastServerUpdate: taskFilterMain.modified,
        }, { root: true })
      }

      if (taskFilterDone && canApplyServerFilter('tasksFilterDone')) {
        commit('tasksFilterDone/SET_FILTERS', {
          filters: taskFilterDone.body,
          lastServerUpdate: taskFilterDone.modified,
        }, { root: true })
      }

      if (activitiesFilter && canApplyServerFilter('activitiesFilter')) {
        commit('activitiesFilter/SET_FILTERS', {
          filters: activitiesFilter.body,
          lastServerUpdate: activitiesFilter.modified,
        }, { root: true })
      }

      if (plansFilter && canApplyServerFilter('plansFilter')) {
        commit('plansFilter/SET_FILTERS', {
          filters: plansFilter.body,
          modified: plansFilter.modified,
        }, { root: true })
      }

      if (documentsFilter && canApplyServerFilter('documentsFilter')) {
        commit('documentsFilter/SET_FILTERS', {
          filters: documentsFilter.body,
          modified: documentsFilter.modified,
        }, { root: true })
      }

      if (photosFilter && canApplyServerFilter('photosFilter')) {
        commit('photosFilter/SET_FILTERS', {
          filters: photosFilter.body,
          modified: photosFilter.modified,
        }, { root: true })
      }

      if (participantsFilter && canApplyServerFilter('participantsFilter')) {
        commit('participantsFilter/SET_FILTERS', {
          filters: participantsFilter.body,
          modified: participantsFilter.modified,
        }, { root: true })
      }
    },

    async deleteProject ({
      commit,
      state,
    }) {
      await ProjectsApi.deleteProject({
        axios: this.$axios,
        project: state.project,
      })

      commit('RESET_PROJECT')
    },

    async transferProject ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.transferProject({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async acceptProjectOwnershipTransfer ({
      commit,
      state,
    }, { project }) {
      const updatedProject = await ProjectsApi.acceptProjectOwnershipTransfer({
        axios: this.$axios,
        project,
      })

      // We need this check, because accept and cancel can also be triggered on the projects index page
      if (state.project && state.project._id === updatedProject._id) {
        commit('SET_PROJECT', updatedProject)
      }
    },

    async cancelProjectOwnershipTransfer ({
      commit,
      state,
    }, { project }) {
      const updatedProject = await ProjectsApi.cancelProjectOwnershipTransfer({
        axios: this.$axios,
        project,
      })

      if (state.project && state.project._id === updatedProject._id) {
        commit('SET_PROJECT', updatedProject)
      }
    },

    async updateProjectTitle ({
      commit,
      state,
    }, {
      newValue,
      oldValue,
    }) {
      const updatedProject = await ProjectsApi.updateProjectTitle({
        axios: this.$axios,
        project: state.project,
        newValue,
        oldValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateProjectLanguage ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.updateProjectLanguage({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateProjectTimezone ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.updateProjectTimezone({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deleteParticipantSelection ({
      commit,
      state,
    }, {
      participant,
      selectionId,
    }) {
      const updatedParticipant = await ParticipantsApi.deleteGroupSelection({
        axios: this.$axios,
        project: state.project,
        participant,
        selectionId,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantSelection ({
      commit,
      state,
    }, {
      participant,
      selectionGroup,
      category,
    }) {
      const updatedParticipant = await ParticipantsApi.updateGroupSelection({
        axios: this.$axios,
        project: state.project,
        category,
        participant,
        selectionGroup,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async addParticipantSelection ({
      commit,
      state,
    }, {
      participant,
      selectionLabel,
      selectionEmails,
      category,
    }) {
      const updatedParticipant = await ParticipantsApi.addGroupSelection({
        axios: this.$axios,
        project: state.project,
        category,
        participant,
        selectionLabel,
        selectionEmails,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async addParticipant ({
      dispatch,
      commit,
      state,
    }, {
      newParticipant,
      inviteMessage,
    }) {
      const project = state.project

      await ParticipantsApi.addParticipant({
        axios: this.$axios,
        project,
        newParticipant,
        inviteMessage,
      })

      await dispatch('fetch', project._id)
    },

    async updateParticipantUploadAlertStatus ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateAlertStatus({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantPlanDocReport ({
      commit,
      state,
    }, {
      participant,
      newValue,
      authTokenRestricted,
    }) {
      const updatedParticipant = await ParticipantsApi.updatePlanDocReport({
        axios: this.$axios,
        authTokenRestricted,
        project: state.project,
        participant,
        isEnabled: newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantTaskReport ({
      commit,
      state,
    }, {
      participant,
      newValue,
      authTokenRestricted,
    }) {
      const updatedParticipant = await ParticipantsApi.updateTaskReport({
        axios: this.$axios,
        authTokenRestricted,
        project: state.project,
        participant,
        isEnabled: newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantDueDateReport ({
      commit,
      state,
    }, {
      participant,
      newValue,
      authTokenRestricted,
    }) {
      const updatedParticipant = await ParticipantsApi.updateDueDateReport({
        axios: this.$axios,
        authTokenRestricted,
        project: state.project,
        participant,
        isEnabled: newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantWeekStartReport ({
      commit,
      state,
    }, {
      participant,
      containsTasks,
      containsPlans,
      containsDocuments,
    }) {
      const updatedParticipant = await ParticipantsApi.updateWeekStartReport({
        axios: this.$axios,
        project: state.project,
        participant,
        containsTasks,
        containsPlans,
        containsDocuments,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantCanapproverevisions ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateCanapproverevisions({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantSendCopyToMyself ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateSendCopyToMyself({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantShowActivitiesInTaskDetail ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateShowActivitiesInTaskDetail({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantShowActivitiesInPlanDetail ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateShowActivitiesInPlanDetail({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantPermission ({
      commit,
      state,
    }, {
      participant,
      newValue,
      message,
    }) {
      const updatedParticipant = await ParticipantsApi.updatePermission({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
        message,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantEnabled ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateEnabled({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantName ({
      commit,
      state,
    }, {
      participant,
      newFirstName,
      newLastName,
    }) {
      const updatedParticipant = await ParticipantsApi.updateName({
        axios: this.$axios,
        project: state.project,
        participant,
        newFirstName,
        newLastName,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantPhone ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updatePhone({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantAddress ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateAddress({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantNote ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateNote({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantCompany ({
      commit,
      state,
    }, {
      participant,
      newValue,
    }) {
      const updatedParticipant = await ParticipantsApi.updateCompany({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)
    },

    async updateParticipantRoles ({
      commit,
      state,
      dispatch,
    }, {
      participant,
      newValue,
    }) {
      await ParticipantsApi.updateRoles({
        axios: this.$axios,
        project: state.project,
        participant,
        newValue,
      })

      await dispatch('fetch', state.project._id)
    },

    async addPhasetagPlans ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addPhasetagPlans({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async reorderPhasetagsPlans ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.reorderPhasetagsPlans({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addPhasetagDocuments ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addPhasetagDocuments({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async reorderPhasetagsDocuments ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.reorderPhasetagsDocuments({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async reorderLocationProperties ({
      commit,
      state,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.reorderLocationProperties({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updatePlansTaggingRestrictedForUsers ({
      commit,
      state,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updatePlansTaggingRestrictedForUsers({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateDocumentsTaggingRestrictedForUsers ({
      commit,
      state,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateDocumentsTaggingRestrictedForUsers({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateIsTwoFactorAuthRequired ({
      commit,
      state,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateIsTwoFactorAuthRequired({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateTopCategoriesPlansEnabled ({
      commit,
      state,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateTopCategoriesPlansEnabled({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addTopCategoriesPlans ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addTopCategoriesPlans({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async reorderTopCategoriesPlans ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.reorderTopCategoriesPlans({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateTopCategoriesPlans ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateTopCategoriesPlans({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('plans/fetch', state.project._id, { root: true })
    },

    async deleteTopCategoryPlans ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deleteTopCategoryPlans({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async enableTopCategoriesDocuments ({
      commit,
      state,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.enableTopCategoriesDocuments({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addTopCategoriesDocuments ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addTopCategoriesDocuments({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async reorderTopCategoriesDocuments ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.reorderTopCategoriesDocuments({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateTopCategoriesDocuments ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateTopCategoriesDocuments({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('documents/fetch', state.project._id, { root: true })
    },

    async deleteTopCategoryDocuments ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deleteTopCategoryDocuments({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addRole ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addRole({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addTagPlans ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addTagPlans({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addTagTasks ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addTagTasks({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addTagPhotos ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addTagPhotos({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addTagDocuments ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addTagDocuments({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updatePhasetagPlans ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updatePhasetagPlans({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)

      await dispatch('plans/fetch', state.project._id, { root: true })
    },

    async updatePhasetagDocuments ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updatePhasetagDocuments({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('documents/fetch', state.project._id, { root: true })
    },

    async updateTagPlans ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateTagPlans({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('plans/fetch', state.project._id, { root: true })
    },

    async updateTagTasks ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateTagTasks({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('tasks/fetch', { projectId: state.project._id }, { root: true })
    },

    async updateTagPhotos ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateTagPhotos({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('photos/fetch', { projectId: state.project._id }, { root: true })
    },

    async updateTagDocuments ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateTagDocuments({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('documents/fetch', state.project._id, { root: true })
    },

    async updateRole ({
      commit,
      state,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateRole({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deletePhasetagPlans ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deletePhasetagPlans({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deletePhasetagDocuments ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deletePhasetagDocuments({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deleteRole ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deleteRole({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deleteTagPlans ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deleteTagPlans({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deleteTagTasks ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deleteTagTasks({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deleteTagPhotos ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deleteTagPhotos({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deleteTagDocuments ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deleteTagDocuments({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async resendParticipantInvitation ({
      commit,
      state,
    }, {
      participant,
      message,
    }) {
      const updatedParticipant = await ParticipantsApi.resendInvitation({
        axios: this.$axios,
        project: state.project,
        participant,
        message,
      })

      commit('UPDATE_PARTICIPANT', updatedParticipant)

      return updatedParticipant
    },

    async updateLocationPropertyIsEnabled ({
      commit,
      state,
    }, {
      locationProperty,
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateLocationPropertyIsEnabled({
        axios: this.$axios,
        project: state.project,
        locationProperty,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addLocationValue ({
      commit,
      state,
    }, {
      locationProperty,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.addLocationValue({
        axios: this.$axios,
        project: state.project,
        locationProperty,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async deleteLocationValue ({
      commit,
      state,
      dispatch,
    }, {
      locationProperty,
      value,
    }) {
      const updatedProject = await ProjectsApi.deleteLocationValue({
        axios: this.$axios,
        project: state.project,
        locationProperty,
        value,
      })

      commit('SET_PROJECT', updatedProject)

      await dispatch('locationLayers/fetch', { projectId: state.project._id }, { root: true })
    },

    async updateLocationValue ({
      commit,
      state,
      dispatch,
    }, {
      locationProperty,
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateLocationValue({
        axios: this.$axios,
        project: state.project,
        locationProperty,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)

      await Promise.all([
        dispatch('tasks/fetch', { projectId: state.project._id }, { root: true }),
        dispatch('locationLayers/fetch', { projectId: state.project._id }, { root: true }),
      ])
    },

    async reorderLocationValues ({
      commit,
      state,
    }, {
      locationProperty,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.reorderLocationValues({
        axios: this.$axios,
        project: state.project,
        locationProperty,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateLocationLabel ({
      commit,
      state,
    }, {
      locationProperty,
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateLocationLabel({
        axios: this.$axios,
        project: state.project,
        locationProperty,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addTaskType ({
      commit,
      state,
    }, { newValue }) {
      const updatedProject = await ProjectsApi.addTaskType({
        axios: this.$axios,
        project: state.project,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updateTaskType ({
      commit,
      state,
      dispatch,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.updateTaskType({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('tasks/fetch', { projectId: state.project._id }, { root: true })
    },

    async deleteTaskType ({
      commit,
      state,
    }, { value }) {
      const updatedProject = await ProjectsApi.deleteTaskType({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async reorderTaskTypes ({
      commit,
      state,
    }, {
      oldValue,
      newValue,
    }) {
      const updatedProject = await ProjectsApi.reorderTaskTypes({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async addPhotoAlbum ({ commit, state }, { newValue }) {
      const updatedProject = await ProjectsApi.addPhotoAlbums({
        axios: this.$axios,
        project: state.project,
        newValues: [ newValue ],
      })

      commit('SET_PROJECT', updatedProject)
    },

    async updatePhotoAlbum ({ commit, state, dispatch }, { oldValue, newValue }) {
      const updatedProject = await ProjectsApi.updatePhotoAlbum({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
      await dispatch('photos/fetch', { projectId: state.project._id }, { root: true })
    },

    async deletePhotoAlbum ({ commit, state }, { value }) {
      const updatedProject = await ProjectsApi.deletePhotoAlbum({
        axios: this.$axios,
        project: state.project,
        value,
      })

      commit('SET_PROJECT', updatedProject)
    },

    async reorderPhotoAlbums ({ commit, state }, { oldValue, newValue }) {
      const updatedProject = await ProjectsApi.reorderPhotoAlbums({
        axios: this.$axios,
        project: state.project,
        oldValue,
        newValue,
      })

      commit('SET_PROJECT', updatedProject)
    },
  },
}
